import { useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const getPhone = () => {
  try { return JSON.parse(localStorage.getItem('gynovate_signup') || '{}').phone || ''; } catch { return ''; }
};

export async function streamCoach({ message, context = 'coach', history = [], onChunk, signal }) {
  const res = await fetch(`${API_BASE}/api/health-hub/coach`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone: getPhone(), message, context, history }),
    signal,
  });
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split('\n');
    buf = lines.pop();
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const d = line.slice(6).trim();
      if (d === '[DONE]') return;
      try {
        const p = JSON.parse(d);
        if (p.type === 'chunk') onChunk(p.content);
      } catch { /* partial json */ }
    }
  }
}

export function useCoachStream() {
  const [output, setOutput] = useState('');
  const [running, setRunning] = useState(false);
  const abortRef = useRef(null);
  const run = async (message, context) => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setOutput('');
    setRunning(true);
    try {
      await streamCoach({ message, context, signal: ctrl.signal, onChunk: (t) => setOutput((o) => o + t) });
    } catch (e) {
      if (e.name !== 'AbortError') setOutput('Something went wrong. Please try again.');
    }
    setRunning(false);
  };
  const stop = () => abortRef.current?.abort();
  return { output, running, run, stop, setOutput };
}

export const AIOutput = ({ text, testId }) => (
  <div data-testid={testId || 'health-ai-output'} className="prose prose-sm max-w-none text-[#33402F] leading-relaxed [&_strong]:text-[#2E4A3B] [&_li]:my-1 [&_p]:my-2 [&_ul]:my-2">
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
  </div>
);

export const BackBar = ({ title, onBack }) => (
  <div className="flex items-center gap-3 mb-5">
    <button
      onClick={onBack}
      data-testid="health-back-btn"
      className="w-10 h-10 rounded-full bg-white border border-[#E3E5D8] shadow-sm flex items-center justify-center text-[#2E4A3B] hover:bg-[#EDF2EB] transition-colors"
      aria-label="Back"
    >
      ←
    </button>
    <h2 className="font-accent text-2xl text-[#273127]">{title}</h2>
  </div>
);

export const Chip = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2.5 rounded-full text-sm font-semibold transition-colors border ${
      active
        ? 'bg-[#2E4A3B] text-white border-[#2E4A3B] shadow-md'
        : 'bg-white text-[#44513F] border-[#E3E5D8] hover:border-[#9DB8A0]'
    }`}
  >
    {children}
  </button>
);

export const Card = ({ children, className = '', testId, onClick }) => (
  <div
    onClick={onClick}
    data-testid={testId}
    className={`bg-white rounded-3xl border border-[#E8EADE] shadow-[0_8px_30px_-12px_rgba(46,74,59,0.12)] ${onClick ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_14px_40px_-12px_rgba(46,74,59,0.2)] transition-transform duration-200' : ''} ${className}`}
  >
    {children}
  </div>
);

export const PrimaryBtn = ({ children, onClick, disabled, testId, className = '' }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={`px-6 py-3.5 rounded-full bg-[#2E4A3B] text-white font-bold text-sm tracking-wide shadow-lg hover:bg-[#3a6049] active:scale-[0.98] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
  >
    {children}
  </button>
);

const stripMd = (t) => (t || '').replace(/\*\*(.+?)\*\*/g, '*$1*').replace(/^#+\s*/gm, '').replace(/^[-•]\s*/gm, '• ').trim();

export const waShare = (text, source = 'Gynovate Health') => {
  window.open(`https://wa.me/?text=${encodeURIComponent(stripMd(text) + `\n\n— via ${source} · Gynovate AI/SI`)}`, '_blank');
};

export const WhatsAppBtn = ({ text, testId, label = 'Share on WhatsApp' }) => (
  <button
    onClick={() => waShare(text)}
    data-testid={testId || 'whatsapp-share-btn'}
    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white shadow-md active:scale-95 transition-transform"
    style={{ background: 'linear-gradient(135deg,#25D366,#128C7E)' }}
  >
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
    {label}
  </button>
);
